const INVALID_FONT_KEY_CHARS = /[.#$[\]/]/g;
const FONT_MIME_BY_EXTENSION = {
  ttf: "font/ttf",
  otf: "font/otf",
  woff: "font/woff",
  woff2: "font/woff2",
};
const loadedFontFaces = new Set();

export const ADD_FONT_VALUE = "__add_font__";

export const normalizeFontName = ({ name } = {}) =>
  String(name || "")
    .replace(/\s+/g, " ")
    .trim();

export const toFontKey = ({ name } = {}) =>
  normalizeFontName({ name })
    .toLowerCase()
    .replace(INVALID_FONT_KEY_CHARS, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");

export const validateFontName = ({ name } = {}) => {
  const fontName = normalizeFontName({ name });
  if (!fontName) return "required";
  if (!toFontKey({ name: fontName })) return "invalidName";
  return null;
};

export const getFontMimeType = ({ file } = {}) => {
  const extension = String(file?.name || "")
    .split(".")
    .pop()
    ?.toLowerCase();
  return FONT_MIME_BY_EXTENSION[extension] || null;
};

const titleCaseSlug = (slug) =>
  String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const upsertFont = ({ fonts, name, url, mimeType = "", key = "" }) => {
  const fontName = normalizeFontName({ name });
  if (!fontName || !url) return;
  fonts[fontName] = {
    name: fontName,
    url,
    mimeType,
    key: key || toFontKey({ name: fontName }),
  };
};

export const flattenFontsNode = (node = {}) => {
  const fonts = {};

  Object.entries(node || {}).forEach(([key, value]) => {
    if (!value || typeof value !== "object") return;

    if (typeof value.url === "string" && value.url) {
      upsertFont({
        fonts,
        name: value.name || key,
        url: value.url,
        mimeType: value.mimeType || "",
        key: value.key || key,
      });
      return;
    }

    const family = titleCaseSlug(key);
    if (typeof value.regular === "string" && value.regular) {
      upsertFont({
        fonts,
        name: `${family} Regular`,
        url: value.regular,
      });
    }
    if (typeof value.bold === "string" && value.bold) {
      upsertFont({
        fonts,
        name: `${family} Bold`,
        url: value.bold,
      });
    }
  });

  return fonts;
};

export const mergeFontCatalogs = ({
  commonFonts = {},
  advertiserFonts = {},
} = {}) => {
  const merged = {
    ...flattenFontsNode(commonFonts),
    ...flattenFontsNode(advertiserFonts),
  };

  return Object.values(merged).sort((left, right) =>
    left.name.localeCompare(right.name, undefined, { sensitivity: "base" })
  );
};

export const loadFontFaces = async ({ fonts = [] } = {}) => {
  await Promise.all(
    fonts.map(async (font) => {
      if (!font?.name || !font?.url || loadedFontFaces.has(font.name)) return;
      try {
        const fontFace = new FontFace(
          font.name,
          `url(${JSON.stringify(font.url)})`
        );
        await fontFace.load();
        document.fonts.add(fontFace);
        loadedFontFaces.add(font.name);
      } catch (_error) {
        // Preview load is best-effort; the stored name remains the ref.
      }
    })
  );
};
